import type { ReactNode } from 'react';
import { getUnifiedContractStatus, UNIFIED_STATUS_LABELS, UNIFIED_STATUS_VARIANTS } from '@/features/contracts/types/contract.types';

export type StatusVariant =
  | 'active'
  | 'expired'
  | 'cancelled'
  | 'suspended'
  | 'pending_signature'
  | 'sent'
  | 'signed'
  | 'handwritten'
  | 'completed'
  | 'in_progress'
  | 'no_action'
  | 'draft'
  | 'info'
  | 'error'
  | 'default';

const VARIANT_CLASSES: Record<StatusVariant, string> = {
  active: 'tw-bg-green-100 tw-text-green-800',
  expired: 'tw-bg-red-100 tw-text-red-800',
  cancelled: 'tw-bg-gray-100 tw-text-gray-800',
  suspended: 'tw-bg-yellow-100 tw-text-yellow-800',
  pending_signature: 'tw-bg-orange-100 tw-text-orange-800',
  sent: 'tw-bg-blue-100 tw-text-blue-800',
  signed: 'tw-bg-green-100 tw-text-green-800',
  handwritten: 'tw-bg-purple-100 tw-text-purple-800',
  completed: 'tw-bg-teal-100 tw-text-teal-800',
  in_progress: 'tw-bg-blue-100 tw-text-blue-800',
  no_action: 'tw-bg-red-100 tw-text-red-800',
  draft: 'tw-bg-gray-100 tw-text-gray-600',
  info: 'tw-bg-blue-100 tw-text-blue-800',
  error: 'tw-bg-red-100 tw-text-red-800',
  default: 'tw-bg-gray-100 tw-text-gray-800',
};

interface StatusBadgeProps {
  label: string;
  variant: StatusVariant;
  subLabel?: string;
  icon?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}

export function StatusBadge({
  label,
  variant,
  subLabel,
  icon,
  size = 'sm',
  className = '',
}: StatusBadgeProps) {
  const sizeClasses = size === 'sm'
    ? 'tw-px-2.5 tw-py-0.5 tw-text-xs'
    : 'tw-px-3 tw-py-1 tw-text-sm';

  return (
    <span className={`tw-inline-flex tw-flex-col tw-items-start tw-gap-0.5 ${className}`}>
      <span
        className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-font-medium ${sizeClasses} ${VARIANT_CLASSES[variant]}`}
      >
        {icon}
        {label}
      </span>
      {subLabel && (
        <span className="tw-text-[10px] tw-text-gray-500 tw-ml-1">{subLabel}</span>
      )}
    </span>
  );
}

// Helpers pour mapper les statuts métier → variant

export function contractStatusToVariant(idStatus: number, monthlyPaymentId?: string | null, monthlyPaymentStatus?: string | null, cancelationRequested?: boolean): StatusVariant {
  const unified = getUnifiedContractStatus({ idStatus, monthlyPaymentId, monthlyPaymentStatus, cancelationRequested });
  return (UNIFIED_STATUS_VARIANTS[unified] || 'default') as StatusVariant;
}

export function contractStatusLabel(idStatus: number, monthlyPaymentId?: string | null, monthlyPaymentStatus?: string | null, cancelationRequested?: boolean): string {
  const unified = getUnifiedContractStatus({ idStatus, monthlyPaymentId, monthlyPaymentStatus, cancelationRequested });
  return UNIFIED_STATUS_LABELS[unified] || 'Inconnu';
}

export function signatureLabel(signature?: { isSent?: boolean; isIntegrated?: boolean; isHandwritten?: boolean }): string | undefined {
  if (!signature) return undefined;
  if (signature.isIntegrated) return 'Signée';
  if (signature.isHandwritten) return 'Manuscrite';
  if (signature.isSent) return 'Envoyée';
  return undefined;
}

export function deliveryProofStateToVariant(state: string): StatusVariant {
  switch (state) {
    case 'pending_signature': return 'pending_signature';
    case 'sent': return 'sent';
    case 'signed': return 'signed';
    case 'canceled': return 'cancelled';
    case 'draft': return 'draft';
    default: return 'default';
  }
}

export function deliveryProofStateLabel(state: string): string {
  switch (state) {
    case 'pending_signature': return 'En attente signature';
    case 'sent': return 'Envoyée';
    case 'signed': return 'Signée';
    case 'canceled': return 'Annulée';
    case 'draft': return 'À finaliser';
    default: return state;
  }
}

export function savStatusToVariant(status: string): StatusVariant {
  switch (status) {
    case 'ACTIVE': return 'in_progress';
    case 'COMPLETED': return 'completed';
    case 'CANCELLED': return 'no_action';
    default: return 'default';
  }
}

export function savStatusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'En cours';
    case 'COMPLETED': return 'Terminé';
    case 'CANCELLED': return 'Sans suite';
    default: return status;
  }
}
